import axios, { AxiosRequestConfig } from "axios";
import { serialize } from "object-to-formdata";
import type { Composer } from "vue-i18n";
import Swal from "sweetalert2";
import { notify } from "@kyvg/vue3-notification";
declare const BASEPATH : string; // Ensure TypeScript knows about it

/** Laravel/API payloads use `status: true | 'success'`; plain booleans are accepted. */
function isApiSuccess(res: any): boolean {
  if (!res || typeof res !== "object") return false;
  const s = res.status;
  return s === true || s === "success" || s === 1;
}

/** Second argument was sometimes a full localized sentence; avoid appending English suffixes to it. */
function looksLikeFullLocalizedMessage(action: string): boolean {
  const t = action?.trim();
  if (!t) return false;
  if (/[\u0600-\u06FF]/.test(t)) return true;
  if (t.length > 40) return true;
  return false;
}

/** Supports `{ field: string[] }`, nested arrays from PHP/validation, and mixed shapes. */
function flattenApiErrors(errors: unknown): string[] {
  const out: string[] = [];

  const pushString = (s: unknown) => {
    if (typeof s === "string" && s.trim()) out.push(s.trim());
  };

  const walk = (node: unknown): void => {
    if (node == null) return;
    if (typeof node === "string") {
      pushString(node);
      return;
    }
    if (Array.isArray(node)) {
      for (const item of node) walk(item);
      return;
    }
    if (typeof node === "object") {
      for (const key of Object.keys(node as object)) {
        walk((node as Record<string, unknown>)[key]);
      }
    }
  };

  walk(errors);
  return out;
}

/**
 * When Laravel cannot resolve a translation it returns the key (e.g. majorseason.overlapping_date_range).
 * Vue i18n (static JSON + /translate DB merge) can still resolve the same path for display.
 */
function looksLikeI18nKey(message: string): boolean {
  const m = message?.trim();
  if (!m) return false;
  if (/[\u0600-\u06FF]/.test(m)) return false;
  if (m.includes(" ")) return false;
  return /^[a-zA-Z][a-zA-Z0-9_]*(\.[a-zA-Z0-9_]+)+$/.test(m);
}

function resolveDisplayMessage(raw: string, t: Composer["t"]): string {
  const msg = raw?.trim();
  if (!msg) return msg;
  if (looksLikeI18nKey(msg)) {
    return String(t(msg as Parameters<Composer["t"]>[0]));
  }
  return msg;
}

function showPoupNotifications(
  res: any,
  action: string,
  successMessage: string | undefined,
  t: Composer["t"]
): void {
  const success = isApiSuccess(res);
  if (success) {
    let title: string;
    let text: string;
    if (successMessage != null && successMessage !== "") {
      title = String(t("common.success"));
      text = successMessage;
    } else if (looksLikeFullLocalizedMessage(action)) {
      title = String(t("common.success"));
      text = action.trim();
    } else {
      const a = action.trim();
      title = a ? `${a} ${String(t("common.successfully"))}` : String(t("common.success"));
      text = String(t("common.operation_completed"));
    }
    notify({ title, text, type: "success" });
    return;
  }

  const flat = flattenApiErrors(res?.errors);
  const primary: string[] =
    flat.length > 0
      ? flat
      : res?.message
        ? [String(res.message)]
        : [];

  if (primary.length > 0) {
    primary.forEach((msg) => {
      notify({
        title: String(t("common.error")),
        text: resolveDisplayMessage(msg, t),
        type: "error",
      });
    });
  } else {
    notify({
      title: String(t("common.error")),
      text: String(t("common.unknown_error")),
      type: "error",
    });
  }
}

export default class API {
  public route: string;
  private baseUrl: string;
  private options: {
    indices: boolean;
    nullsAsUndefineds: boolean;
    booleansAsIntegers: boolean;
    allowEmptyArrays: boolean;
  };
  private headers: Record<string, string>;
  ignoreFunction: string[] = [];

  constructor(route: string, baseUrl?: string | null) {
   this.baseUrl = baseUrl ?? BASEPATH;

    
    this.route = route;
    this.options = {
      indices: true,
      nullsAsUndefineds: false,
      booleansAsIntegers: false,
      allowEmptyArrays: true,
    };
    /**
     * Do NOT set Content-Type for multipart FormData here. Axios (and the browser) must set
     * `multipart/form-data; boundary=----...` to match the actual body. A bare `multipart/form-data`
     * or a random boundary breaks file uploads on the server.
     */
    this.headers = {};
  }

  public getBaseUrl(): string {
    return this.baseUrl;
  }

  async from(
    data: Record<string, any>,
    returnData = false,
    formData = true,
    stopPopup = false
  ): Promise<any> {
    let res: any;

    // Use JSON headers when formData is false
    const headers = formData ? this.headers : { 'Content-Type': 'application/json' };

    let action = "Insert";
    if (data.id && data.id !== "") {
      action = "Updata";
      if (formData) { 
        res = await this.update(data, headers, formData);
      } else {        
        res = await this.updataPut(data, headers);
      }
    } else {
      res = await this.insert(data, headers, formData);
    }

    if (returnData) {
      if (!stopPopup) return { status: this.poup(res,action), data: res };
      return { data: res };
    }

    if (!stopPopup) return this.poup(res,action);
  }

  
  


  /*//////////////////////////////////////////////////*/
  async fetchFileData(
    data: Record<string, any>,
    headers: Record<string, string> = {},
    formDataType = true
  ): Promise<any> {
    try {
      const formData = serialize(data, this.options);
      axios.defaults.baseURL = this.baseUrl;
      const response = await axios.post(
        `/${this.route}-fetchFileData`,
        formDataType ? formData : data,
        { headers }
      );
      return response.data;
    } catch (error: any) {
      if (error.response) {
        return error.response.data;
      }
    }
  }




  async importFileData(
    data: Record<string, any>,
    headers: Record<string, string> = {},
    formDataType = true
  ): Promise<any> {
    try {
      axios.defaults.baseURL = this.baseUrl;
      const response = await axios.post(
        `/${this.route}-storeFileData`,
       data
      );
      return response.data;
    } catch (error: any) {
      if (error.response) {
        return error.response.data;
      }
    }
  }
  /*//////////////////////////////////////////////////*/





  async getAll(params?: Record<string, any>): Promise<any> {
    try {
      axios.defaults.baseURL = this.baseUrl;
      
      const response = await axios.get(`/${this.route}`, { params });
      return response.data;
    } catch (error: any) {
      if (error.response) {
        return error.response.data;
      }
      throw error;
    }
  }

  async group(params: Record<string, any>): Promise<any> {
    try {
      axios.defaults.baseURL = this.baseUrl;
      const response = await axios.get(`/${this.route}-group`, { params });
      return response.data;
    } catch (error: any) {
      throw error;
    }
  }

  async show(id: string, params?: Record<string, any>): Promise<any> {
    try {
      axios.defaults.baseURL = this.baseUrl;
      const response = await axios.get(`/${this.route}/${id}`, { params });
      return response.data;
    } catch (error: any) {
      if (error.response) {
        return error.response.data;
      }
      throw error;
    }
  }

  async insert(
    data: Record<string, any>,
    headers: Record<string, string> = {},
    formDataType = true
  ): Promise<any> {
    try {
      // If data is already a FormData instance, use it directly
      // Otherwise, serialize it using object-to-formdata
      const formData = data instanceof FormData ? data : serialize(data, this.options);
      axios.defaults.baseURL = this.baseUrl;
      const response = await axios.post(
        `/${this.route}`,
        formDataType ? formData : data,
        { headers }
      );
      return response.data;
    } catch (error: any) {
      if (error.response) {
        return error.response.data;
      }
    }
  }

  async update(
    data: Record<string, any>,
    headers: Record<string, string> = {},
    formDataType = true
  ): Promise<any> {
    // Validate that ID exists in data
    if (!data || !data.id) {
      console.error('API.update: Missing ID in data:', data);
      return {
        status: false,
        errors: [['Missing ID in update data']],
        message: 'Missing ID in update data'
      };
    }

    headers = { ...this.headers, ...headers };
    const formData = serialize(data, this.options);
    const id = formData.get("id");
    
    // Double-check that ID was extracted correctly
    if (!id || id === 'null' || id === 'undefined') {
      console.error('API.update: Invalid ID extracted from formData:', id, 'Original data:', data);
      return {
        status: false,
        errors: [['Invalid ID in update request']],
        message: 'Invalid ID in update request'
      };
    }

    formData.append("_method", "PUT");

    try {
      axios.defaults.baseURL = this.baseUrl;
      const url = `/${this.route}/${id}`;
      const response = formDataType
        ? await axios.post(url, formData, { headers })
        : await axios.put(url, data, {
            headers: { ...headers, "Content-Type": "application/json" },
          });
      return response.data;
    } catch (error: any) {
      console.error('API.update: Request failed:', error);
      if (error.response) {
        return error.response.data;
      }
      // Return error if no response (network error, etc.)
      return {
        status: false,
        errors: [[error.message || 'Network error']],
        message: error.message || 'Network error occurred'
      };
    }
  }

  /**
 * Added By Ahmed Nour
 * Sends a POST request to update or create a resource.
 * 
 * This method attempts to either update an existing resource
 * or create a new one by sending a POST request to the 
 * `updateOrCreate` endpoint of the current route.
 * 
 * @returns {Promise<any>} The response data from the server.
 * 
 * @throws Will log an error message if the request fails.
 */

  async CreateOrUpdate(data: Record<string, any>, headers: Record<string, string> = {}, formData = true) {
    try {
      const formDataObject = formData ? serialize(data) : data;
      axios.defaults.baseURL = this.baseUrl;
      const response = await axios.post(`/${this.route}/updateOrCreate`, formDataObject, { headers });
      return response.data;
    } catch (error: any) {
      console.error("Error in CreateOrUpdate:", error.response?.data || error.message);
      return error.response?.data;
    }
  }

  async updataPut(
    data: Record<string, any>,
    headers: Record<string, string> = {}
  ): Promise<any> {
    try {
      axios.defaults.baseURL = this.baseUrl;
      const response = await axios.put(`/${this.route}/${data.id}`, data, {
        headers,
      });
      return response.data;
    } catch (error: any) {
      if (error.response) {
        return error.response.data;
      }
    }
  }

  async delete(
    id: string,
    recordIds: string[] = [],
    allowConfirmation = true,
    stopPopup = false
  ): Promise<any> {
    try {
      let result: any;

      if (allowConfirmation) {
        const { i18n } = await import("@/main");
        const { t } = i18n.global as Composer;
        result = await Swal.fire({
          title: t("common.confirm_link"),
          icon: "warning",
          showCancelButton: true,
          cancelButtonText: t("common.cancel"),
          confirmButtonColor: "#0c758d",
          cancelButtonColor: "#ffc107",
          confirmButtonText: t("common.yes_delete_it"),
        });
      } else {
        result = { isConfirmed: true };
      }

      if (result.isConfirmed) {
          axios.defaults.baseURL = this.baseUrl;
          const response = await axios.delete(`/${this.route}/${id}`, {
          data: { recordIds },
        });
        if (!stopPopup) this.poup(response.data ,"Delete ");
        return response.data;
      }
    } catch (error: any) {
      if (error.response) {
        // Moustafa Mohamed
        const errorData = error.response.data; // Extract error details and show in the popup
        this.poup(errorData, "Delete");
        return errorData;
      }
    }
  }
    // Mohamed Ibrahiim
  /**
   * @param action Short label for legacy success title (e.g. "Delete ") or a full localized sentence (detected for success-only).
   * @param successMessage When set, success notify uses `common.success` as title and this string as body (preferred for i18n).
   */
  protected poup(
    res: any,
    action: string = "",
    successMessage?: string
  ): boolean | [boolean, any] {
    const ok = isApiSuccess(res);
    void import("@/main")
      .then(({ i18n }) => {
        const { t } = i18n.global as Composer;
        showPoupNotifications(res, action, successMessage, t);
      })
      .catch(() => {
        if (ok) {
          notify({
            title: "Success",
            text:
              successMessage ||
              (looksLikeFullLocalizedMessage(action) ? action.trim() : action.trim() || "OK"),
            type: "success",
          });
        } else {
          const flat = flattenApiErrors(res?.errors);
          const msg = flat[0] || res?.message || "An error occurred.";
          notify({ title: "Error", text: msg, type: "error" });
        }
      });

    return ok ? [true, res] : false;
  }

  exportAllToExcel() {
    return new Promise((resolve, reject) => {
      axios
        .get(`${this.route}/export-data`, {
          responseType: "blob", 
        })
        .then((response) => {
          const url = window.URL.createObjectURL(new Blob([response.data]));
          const link = document.createElement("a");
          link.href = url;
          link.setAttribute("download", "exported_data.xlsx");
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          this.poup({ status: "success" }, "Export");
          resolve(response);
        })
        .catch((error) => {
          this.poup({ status: false, message: "An error occurred while Exporting Data." }, "Error");
          reject(error);
        });
    });
  }
  
}